/**
 * Teams Hub - Entry Point
 * 
 * Real-time team collaboration platform with:
 * - Channels (group discussions, job-linked)
 * - Direct Messages (1:1 conversations)
 * - Notes integration (cross-hub)
 * - People directory with presence
 * 
 * Uses shared packages:
 * - @quotech/auth for authentication (via ProtectedRoute/SSOHandler in App.tsx)
 * - @quotech/api for API client and React Query
 */

import React from 'react'
import ReactDOM from 'react-dom/client'
import { BrowserRouter } from 'react-router-dom'
import { ApiProvider } from '@quotech/api'
import { Toaster } from 'sonner'
import { installChunkErrorHandler, installClientErrorReporter } from '@quotech/ui'
import {
  getAuthHeadersForDiagnostics,
  getOrganisationIdForDiagnostics,
} from '@quotech/auth'
import App from './App'
import { setupApi } from './lib/setupApi'
import './index.css'

// Handle stale chunk 404s after deployment (auto-reload)
installChunkErrorHandler()

// Capture runtime errors + failed fetches to /api/diagnostics/client-error/
// so support agents can root-cause opaque crashes without DevTools.
// See packages/ui/src/diagnostics/clientErrorReporter.ts.
installClientErrorReporter({
  buildSha: import.meta.env.VITE_BUILD_SHA,
  getAuthHeaders: getAuthHeadersForDiagnostics,
  getOrganisationId: getOrganisationIdForDiagnostics,
})

// Initialize API with auth interceptors
setupApi()

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <ApiProvider devtools>
      {/* unstable_useTransitions={false} — see apps/newmark/src/main.tsx. (qf!3010, May 2026) */}
      <BrowserRouter unstable_useTransitions={false}>
        <App />
        <Toaster 
          position="bottom-right" 
          richColors 
          closeButton
          toastOptions={{
            className: 'text-sm',
          }}
        />
      </BrowserRouter>
    </ApiProvider>
  </React.StrictMode>,
)
